/**
 * AGROMX Frontend - API Client
 * Cliente HTTP base para comunicación con el backend
 */

const API_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;

class ApiClient {
  constructor() {
    this.baseUrl = API_URL;
    this.token = localStorage.getItem('accessToken');
  }

  /**
   * Configurar token de autenticación
   */
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('accessToken', token);
    } else {
      localStorage.removeItem('accessToken');
    }
  }

  /**
   * Obtener token actual
   */
  getToken() {
    return this.token || localStorage.getItem('accessToken');
  }

  /** Base URL de la API (misma que POST/GET) para servicios que no usan fetch directo */
  getBaseUrl() {
    return this.baseUrl;
  }

  /**
   * Construir headers
   */
  getHeaders(customHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...customHeaders
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Manejar respuesta
   */
  async handleResponse(response) {
    const data = await response.json();

    if (!response.ok) {
      // Si el token expiró, limpiar y redirigir al login.
      // Excepción: si la URL es /auth/login (intento de iniciar sesión con
      // credenciales malas), NO redirigimos — dejamos que el login muestre
      // su error. Solo redirigimos cuando hay un token previo que expiró.
      if (response.status === 401) {
        const esIntentoLogin = response.url && response.url.includes('/auth/login');
        if (!esIntentoLogin) {
          this.setToken(null);
          // No desuscribir Push aqui: igual que en logout, forzaria un endpoint nuevo al volver a entrar
          // y duplicaria filas en push_subscriptions; el servidor ya filtra por activo en subscribe.
          window.location.href = '/';
        }
      }

      throw {
        status: response.status,
        message: data.error?.message || 'Error en la petición',
        code: data.error?.code,
        details: data.error?.details
      };
    }

    return data;
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}) {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    Object.keys(params).forEach(key => 
      url.searchParams.append(key, params[key])
    );

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * POST request
   */
  async post(endpoint, body = {}) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });

    return this.handleResponse(response);
  }

  /**
   * PUT request
   */
  async put(endpoint, body = {}) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });

    return this.handleResponse(response);
  }

  /**
   * PATCH request
   */
  async patch(endpoint, body = {}) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(body)
    });

    return this.handleResponse(response);
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    return this.handleResponse(response);
  }

  /**
   * Upload de archivo
   */
  async upload(endpoint, formData) {
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.getToken()}`
      },
      body: formData
    });

    return this.handleResponse(response);
  }
}

// Exportar instancia única
export default new ApiClient();
